/**
 * Financeiro — rota `/app/financeiro`.
 *
 * A tela era vitrine: todo indicador era travessão e todo botão estava
 * desabilitado dizendo "o módulo financeiro ainda não existe". Ele passou a
 * existir (migração `0012_venda.sql` + `docs/plano/fluxo-de-venda.md` §2), e
 * agora são três abas de verdade — visão geral, a receber e a pagar — cada uma
 * consumindo a API.
 *
 * As abas que a spec 03 lista e o módulo ainda não entrega (fluxo de caixa,
 * conciliação, DRE, comissão) NÃO estão aqui como aba vazia: o que falta em
 * cada uma está escrito no fim da visão geral. Aba que abre e não faz nada é
 * pior do que aba nenhuma — a pessoa conclui que a tela quebrou, e o motivo
 * real (falta dado, não falta código de tela) fica invisível.
 *
 * O front não autoriza nada (CONSTITUICAO.md §4.1): quem recusa lançar ou dar
 * baixa é `porteiro.Guardar`, e a recusa chega como erro da API mostrado no
 * ponto da ação. Nenhum botão daqui decide permissão.
 */
import { useRef, useState, type KeyboardEvent as EventoDeTecla } from 'react'
import { useLocation } from 'react-router-dom'

import { Icone, type NomeIcone } from '../../componentes/Icone'
import type { Lado, RecorteDeContas } from '../../plataforma/financeiro'
import { LayoutApp } from '../app/LayoutApp'
import { ListaDeContas } from './ListaDeContas'
import { PainelContas } from './PainelContas'
import { VisaoGeral } from './VisaoGeral'

type Aba = 'visao' | Lado | 'contas'

const ABAS: { chave: Aba; rotulo: string; icone: NomeIcone }[] = [
  { chave: 'visao', rotulo: 'Visão geral', icone: 'space_dashboard' },
  { chave: 'receber', rotulo: 'A receber', icone: 'payments' },
  { chave: 'pagar', rotulo: 'A pagar', icone: 'receipt_long' },
  // Caixa e banco (migração 0023) — a conta que carrega o saldo, não o
  // título a pagar/receber. `storefront` para não repetir o ícone de
  // 'payments' já usado em "A receber".
  { chave: 'contas', rotulo: 'Contas', icone: 'storefront' },
]

export function TelaFinanceiro() {
  /*
   * A aba pode chegar pela ROTA, no state da navegação.
   *
   * É como a tela de lançamento devolve a pessoa para o lado de onde ela veio:
   * quem lançou uma conta a pagar volta para "A pagar", e não para o resumo do
   * mês. Só o valor INICIAL vem de lá — depois disso quem manda é o clique.
   */
  const local = useLocation()
  const abaDaRota = (local.state as { aba?: Aba } | null)?.aba
  const [aba, setAba] = useState<Aba>(abaDaRota ?? 'visao')
  /**
   * O recorte com que a aba vai abrir quando o clique veio de um KPI da visão
   * geral. Some depois de aplicado — quem volta para a aba pelo próprio botão
   * espera a lista inteira, não o último recorte de dois cliques atrás.
   */
  const [recorte, setRecorte] = useState<RecorteDeContas | ''>('')

  function abrirComRecorte(lado: Lado, alvo: RecorteDeContas) {
    setRecorte(alvo)
    setAba(lado)
  }

  function trocarAba(alvo: Aba) {
    setRecorte('')
    setAba(alvo)
  }

  return (
    <LayoutApp
      titulo="Financeiro"
      subtitulo="Contas a receber, contas a pagar e o resumo do mês"
    >
      <div className="flex w-full flex-col gap-4">
        <Abas atual={aba} aoTrocar={trocarAba} />

        <div
          role="tabpanel"
          id={`painel-${aba}`}
          aria-labelledby={`aba-${aba}`}
          className="flex w-full flex-col"
        >
          {aba === 'visao' ? (
            <VisaoGeral aoAbrir={abrirComRecorte} />
          ) : aba === 'contas' ? (
            <PainelContas />
          ) : (
            /* `key` com o recorte: vir de "A receber atrasado" quando a aba de
               receber já está montada precisa reiniciar o filtro dela, e um
               estado inicial de useState não muda sozinho. */
            <ListaDeContas key={`${aba}-${recorte}`} lado={aba} recorteInicial={recorte} />
          )}
        </div>
      </div>
    </LayoutApp>
  )
}

/**
 * A faixa de abas do handoff, com o comportamento de teclado que uma tablist
 * de verdade precisa ter: seta move entre abas, e só a aba ativa é alcançável
 * por Tab (roving tabindex). Sem isso, quem navega por teclado percorre três
 * paradas antes de chegar no conteúdo.
 */
function Abas({ atual, aoTrocar }: { atual: Aba; aoTrocar: (aba: Aba) => void }) {
  const faixa = useRef<HTMLDivElement>(null)

  function aoTeclar(evento: EventoDeTecla) {
    const passo = evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0
    if (passo === 0) return
    evento.preventDefault()

    const indice = ABAS.findIndex((a) => a.chave === atual)
    const proxima = ABAS[(indice + passo + ABAS.length) % ABAS.length]
    if (!proxima) return
    aoTrocar(proxima.chave)
    // O foco acompanha a seleção: numa tablist automática, quem chegou na aba
    // pela seta espera continuar de lá com Tab.
    requestAnimationFrame(() => {
      faixa.current?.querySelector<HTMLButtonElement>(`#aba-${proxima.chave}`)?.focus()
    })
  }

  return (
    <div
      ref={faixa}
      role="tablist"
      aria-label="Seções do financeiro"
      onKeyDown={aoTeclar}
      className="flex flex-wrap gap-6 border-b border-borda"
    >
      {ABAS.map((item) => {
        const ativa = item.chave === atual
        return (
          <button
            key={item.chave}
            id={`aba-${item.chave}`}
            type="button"
            role="tab"
            aria-selected={ativa}
            aria-controls={`painel-${item.chave}`}
            tabIndex={ativa ? 0 : -1}
            onClick={() => aoTrocar(item.chave)}
            className={`-mb-px flex items-center gap-2 border-b-2 pb-3 text-[13.5px] font-semibold whitespace-nowrap ${
              ativa
                ? 'border-marca text-marca'
                : 'border-transparent text-texto-suave hover:text-texto'
            }`}
          >
            <Icone nome={item.icone} tamanho={17} />
            {item.rotulo}
          </button>
        )
      })}
    </div>
  )
}
